import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import App from '@/App';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '@/theme';
import { StoreProvider } from '@/store/StoreProvider';
import { useAppStore } from '@/store';
import { queryClient } from '@/lib/queryClient';
import { QueryConnectivityManager } from '@/lib/queryConnectivityManager';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from '@/config/msal';
import MobileBlock from '@/components/MobileBlock';

// Import Bossard corporate fonts
import '@/assets/fonts/fonts.css';

// Initial theme to avoid flash of unstyled content
const defaultTheme = createAppTheme('light');

const ThemeWrapper = () => {
  const { themeMode } = useAppStore();
  const theme = createAppTheme(themeMode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MobileBlock>
        <App />
      </MobileBlock>
    </ThemeProvider>
  );
};

// Await MSAL initialisation before rendering so that handleRedirectPromise
// completes first — this ensures the OAuth state/code params are stripped
// from the URL before the app mounts.
msalInstance.initialize().then(async () => {
  // Explicitly process any pending redirect response. MSAL should clean up
  // OAuth params (code, state, session_state) from the URL after this call.
  await msalInstance.handleRedirectPromise();

  // Fallback: manually strip any remaining OAuth redirect params so they
  // never appear in the address bar after login.
  const oauthParams = ['code', 'state', 'session_state', 'error', 'error_description'];
  const url = new URL(window.location.href);
  const hadParams = oauthParams.some(p => url.searchParams.has(p));
  if (hadParams) {
    oauthParams.forEach(p => url.searchParams.delete(p));
    const cleanUrl = url.pathname + (url.search !== '?' ? url.search : '') + url.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }
}).then(() => {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <QueryConnectivityManager />
        <BrowserRouter>
          <ThemeProvider theme={defaultTheme}>
            <CssBaseline />
          <StoreProvider>
            <ThemeWrapper />
          </StoreProvider>
        </ThemeProvider>
      </BrowserRouter>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </MsalProvider>
  </React.StrictMode>
  );
}).catch(console.error);